import React, { useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Card from 'components/DesignSystem/Card';
import EditButton from 'components/Shared/EditButton';
import PrecosSeTable from 'components/Relatorios/Matriz/PrecosSeTable';
import RichHtml from 'components/RichHtml';
import Selector from 'components/DesignSystem/Selector';

import useMatrizSecaoParams from 'hooks/Relatorios/Matriz/useMatrizSecaoParams';
import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import { useGetMatrizChuvaObservadaQuery } from 'services/api/products/relatorios';

import './index.scss';

const MATRIZ_PADRAO = 'Matriz Padrão';
const MES_PADRAO = 'Janeiro';
const SUBSISTEMA_PADRAO = 'SE/CO';

/**
 * View principal da Matriz (Informe Semanal). Matriz não tem sub-abas — ao
 * contrário de Decomp/Dessem/Intersemanal/Semanal, que usam `secoes/` para
 * organizar várias seções sob um `Outlet` com tabs internas, a rota
 * `semanal/matriz` aponta direto pra este componente único (Task 3c). Mantido
 * em `secoes/ChuvaObservada` (em vez de `Matriz/index.tsx`) para espelhar o
 * padrão de nomenclatura já usado pelos outros módulos (endpoint
 * `getMatrizChuvaObservada`, mock `matriz/chuva-observada.json`) e deixar
 * espaço para futuras seções da Matriz sem precisar reestruturar depois.
 *
 * Os 3 filtros (Matriz/Mês/Subsistema) são estado local (useState), não
 * Redux — mesmo padrão do `subsistema` em `Semanal/secoes/VisaoGeral`. As
 * opções de cada `Selector` vêm de `data.filtros` (populadas só depois que a
 * query resolve pela 1ª vez com os valores-padrão); `data_rodada`/`cenario`
 * vêm do slice `relatoriosMatriz` via `useMatrizSecaoParams`.
 */
export default function ChuvaObservada() {
  const userIsAdmin = useSelector(selectUserIsAdmin);
  const params = useMatrizSecaoParams();

  const [matriz, setMatriz] = useState(MATRIZ_PADRAO);
  const [mes, setMes] = useState(MES_PADRAO);
  const [subsistema, setSubsistema] = useState(SUBSISTEMA_PADRAO);

  const { data, isLoading } = useGetMatrizChuvaObservadaQuery({
    ...params, matriz, mes, subsistema,
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-3">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="matriz-chuva-observada">
      <Card className="mb-3">
        <div className="d-flex gap-3">
          <Selector
            label="Matriz"
            containerClassName="matriz-chuva-observada__selector"
            value={matriz}
            setValue={setMatriz}
          >
            {data.filtros.matrizes.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </Selector>
          <Selector
            label="Mês"
            containerClassName="matriz-chuva-observada__selector"
            value={mes}
            setValue={setMes}
          >
            {data.filtros.meses.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </Selector>
          <Selector
            label="Subsistema"
            containerClassName="matriz-chuva-observada__selector"
            value={subsistema}
            setValue={setSubsistema}
          >
            {data.filtros.subsistemas.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Selector>
        </div>
      </Card>

      <Row className="g-3 matriz-chuva-observada__row">
        <Col md="auto" className="matriz-chuva-observada__texto-col">
          <Card
            className="h-100"
            title="TEXTO"
            actions={userIsAdmin && (
              <EditButton id="edit-matriz-texto" onClick={() => undefined} />
            )}
          >
            <div className="matriz-chuva-observada__scroll">
              <RichHtml html={data.texto.conteudo_html} className="w-100 ql-editor" />
            </div>
          </Card>
        </Col>
        <Col className="matriz-chuva-observada__tabelas-col">
          <Card className="h-100" title="Chuva Observada">
            <div className="matriz-chuva-observada__scroll d-flex flex-column gap-4">
              {data.tabelas.map((tabela, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <PrecosSeTable key={index} tabela={tabela} />
              ))}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
